import { Fn }			from "ts-base/fn";
import { Dates }		from "ts-base/dates";
import { Equal }		from "ts-base/equal";
import { Comparator }	from "ts-base/comparator";

import { DateOffset }	from "ts-base/time/dateOffset";
import { WeekdayName }	from "ts-base/time/weekdayName";

export type DateTime	= Readonly<{
	// 1 is AD 1, 2 is AD 2, 0 is BC 1, -1 is BC 2 and so on
	year:	number,
	// month is 1-indexed as opposed to Date where the month field is 0-indexed!
	month:	number,
	day:	number,
	hour:	number,
	minute:	number,
	second:	number,
	milli:	number,
}>;

export namespace DateTime {
	export const normalized	= (self:DateTime):DateTime|null	=>
		isNormal(self) ? self : null;

	export const isNormal	= (self:DateTime):self is DateTime	=>
		equal(self, utc.normalize(self));

	export const weekdayName	= (self:DateTime):WeekdayName	=>
		Dates.utc.weekdayName(utc.toDate(self));

	//-----------------------------------------------------------------------------
	//## typeclasses

	export const equal:Equal<DateTime>	= (a:DateTime, b:DateTime):boolean	=>
		a.year		=== b.year		&&
		a.month		=== b.month		&&
		a.day		=== b.day		&&
		a.hour		=== b.hour		&&
		a.minute	=== b.minute	&&
		a.second	=== b.second	&&
		a.milli		=== b.milli;

	export const compare:Comparator<DateTime> = (a:DateTime, b:DateTime) =>
		a.year		< b.year	? -1 : a.year		> b.year	? +1 :
		a.month		< b.month	? -1 : a.month		> b.month	? +1 :
		a.day		< b.day		? -1 : a.day		> b.day		? +1 :
		a.hour		< b.hour	? -1 : a.hour		> b.hour	? +1 :
		a.minute	< b.minute	? -1 : a.minute		> b.minute	? +1 :
		a.second	< b.second	? -1 : a.second		> b.second	? +1 :
		a.milli		< b.milli	? -1 : a.milli		> b.milli	? +1 :
		0;

	//-----------------------------------------------------------------------------

	export type Precision	= "milli"|"second"|"minute"|"hour"|"day"|"month"|"year";

	export const floorTo	= (dateTime:DateTime, precision:Precision):DateTime	=> ({
		milli:	[																"milli" ].includes(precision) ? dateTime.milli	: 0,
		second:	[													"second",	"milli" ].includes(precision) ? dateTime.second	: 0,
		minute:	[										"minute",	"second",	"milli" ].includes(precision) ? dateTime.minute	: 0,
		hour:	[								"hour",	"minute",	"second",	"milli" ].includes(precision) ? dateTime.hour	: 0,
		day:	[						"day",	"hour",	"minute",	"second",	"milli" ].includes(precision) ? dateTime.day	: 0,
		month:	[			"month",	"day",	"hour",	"minute",	"second",	"milli" ].includes(precision) ? dateTime.month	: 0,
		year:	[ "year",	"month",	"day",	"hour",	"minute",	"second",	"milli" ].includes(precision) ? dateTime.year	: 0,
	});

	export const later	= (self:DateTime, offset:DateOffset.Partial):DateTime	=>
		shift(self, DateOffset.fromPartial(offset));

	export const earlier	= (self:DateTime, offset:DateOffset.Partial):DateTime	=>
		shift(self, DateOffset.negate(DateOffset.fromPartial(offset)));

	const shift	= (self:DateTime, offset:DateOffset):DateTime	=>
		utc.normalize({
			year:	self.year	+ offset.years,
			month:	self.month	+ offset.months,
			day:	self.day	+ offset.days,
			hour:	self.hour	+ offset.hours,
			minute:	self.minute	+ offset.minutes,
			second:	self.second	+ offset.seconds,
			milli:	self.milli	+ offset.millis,
		});

	//-----------------------------------------------------------------------------

	export type BuiltinZone	= Readonly<{
		fromDate:	Fn<Date, DateTime>,
		toDate:		Fn<DateTime, Date>,

		// ensure all fields have valid values
		normalize:	Fn<DateTime, DateTime>,
	}>;

	const builtinZone	= (
		fromDate:	Fn<Date, DateTime>,
		toDate:		Fn<DateTime, Date>,
	):BuiltinZone	=> ({
		fromDate:	fromDate,
		toDate:		toDate,
		normalize:	(it) => fromDate(toDate(it)),
	});

	export const local:BuiltinZone	=
		builtinZone(
			(it:Date):DateTime	=> ({
				year:	it.getFullYear(),
				month:	it.getMonth()+1,
				day:	it.getDate(),
				hour:	it.getHours(),
				minute:	it.getMinutes(),
				second:	it.getSeconds(),
				milli:	it.getMilliseconds(),
			}),
			(it:DateTime):Date	=> {
				// Date.constructor assumes 19xx when the year passed is below 100
				if (it.year >= 100) {
					return new Date(
						it.year,
						it.month-1,
						it.day,
						it.hour,
						it.minute,
						it.second,
						it.milli,
					);
				}
				else {
					const out	= new Date();
					out.setFullYear(it.year);
					// these have to be set at once or the month will be auto-corrected when the day is too large
					out.setMonth(it.month-1, it.day);
					out.setHours(it.hour);
					out.setMinutes(it.minute);
					out.setSeconds(it.second);
					out.setMilliseconds(it.milli);
					return out;
				}
			}
		);

	export const utc:BuiltinZone	=
		builtinZone(
			(it:Date):DateTime	=> ({
				year:	it.getUTCFullYear(),
				month:	it.getUTCMonth()+1,
				day:	it.getUTCDate(),
				hour:	it.getUTCHours(),
				minute:	it.getUTCMinutes(),
				second:	it.getUTCSeconds(),
				milli:	it.getUTCMilliseconds(),
			}),
			(it:DateTime):Date	=> {
				// Date.UTC assumes 19xx when the year passed is below 100
				if (it.year >= 100) {
					return new Date(
						Date.UTC(
							it.year,
							it.month-1,
							it.day,
							it.hour,
							it.minute,
							it.second,
							it.milli,
						)
					);
				}
				else {
					const out	= new Date();
					out.setUTCFullYear(it.year);
					// these have to be set at once or the month will be auto-corrected when the day is too large
					out.setUTCMonth(it.month-1, it.day);
					out.setUTCHours(it.hour);
					out.setUTCMinutes(it.minute);
					out.setUTCSeconds(it.second);
					out.setUTCMilliseconds(it.milli);
					return out;
				}
			}
		);
}
